/**
 * NF Projekt (Wishlist) Functionality
 * For Collection Configurator template
 */

(function() {
  'use strict';

  // Get collection handle from DOM to scope storage per collection
  const getCollectionHandle = () => {
    const wrapper = document.getElementById('collection-configurator');
    return wrapper?.dataset.collectionHandle || '';
  };

  // Storage key scoped by collection handle
  const getStorageKey = () => {
    const handle = getCollectionHandle();
    return handle ? `nf_projekt_items_${handle}` : 'nf_projekt_items';
  };

  const STRINGS = window.nfProjektStrings || {};
  const ASSETS = window.nfProjektAssets || {};

  const getString = (path) => {
    if (!path) return '';
    return path.split('.').reduce((acc, key) => {
      if (acc && Object.prototype.hasOwnProperty.call(acc, key)) {
        return acc[key];
      }
      return null;
    }, STRINGS) || '';
  };

  const formatString = (template, vars) => {
    if (!template) return '';
    return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) => {
      if (!vars) return '';
      const value = vars[key];
      return value === undefined || value === null ? '' : value;
    });
  };

  // Initialize projekt
  const NFProjekt = {
    items: [],

    init() {
      this.loadItems();
      this.bindEvents();
      this.updateUI();
      this.updateAllHeartButtons();
      this.moveMobileBarToBody();
    },

    // Move mobile bar and overlay to body (always needed for mobile functionality)
    // Sidebar only moves to body on mobile to preserve desktop flex layout
    moveMobileBarToBody() {
      const mobileBar = document.querySelector('.nf-projekt-mobile-bar');
      const overlay = document.querySelector('.nf-projekt-overlay');
      const sidebar = document.querySelector('.nf-projekt-sidebar');

      // Mobile bar and overlay always go to body (they're only visible on mobile anyway)
      if (mobileBar && mobileBar.parentNode !== document.body) {
        document.body.appendChild(mobileBar);
        console.log('[NFProjekt] Moved mobile bar to body');
      }

      if (overlay && overlay.parentNode !== document.body) {
        document.body.appendChild(overlay);
        console.log('[NFProjekt] Moved overlay to body');
      }

      // Only move sidebar to body on mobile (< 769px)
      // On desktop, keep it in the flex container for proper layout
      if (window.innerWidth < 769) {
        if (sidebar && sidebar.parentNode !== document.body) {
          document.body.appendChild(sidebar);
          console.log('[NFProjekt] Moved sidebar to body (mobile)');
        }
      } else {
        console.log('[NFProjekt] Sidebar stays in flex container (desktop)');
      }
    },

    // Load items from localStorage (scoped by collection)
    loadItems() {
      try {
        const storageKey = getStorageKey();
        const stored = localStorage.getItem(storageKey);
        this.items = stored ? JSON.parse(stored) : [];
        console.log('[NFProjekt] Loaded items from', storageKey, '- count:', this.items.length);
      } catch (e) {
        this.items = [];
      }
    },

    // Save items to localStorage (scoped by collection)
    saveItems() {
      try {
        const storageKey = getStorageKey();
        localStorage.setItem(storageKey, JSON.stringify(this.items));
        console.log('[NFProjekt] Saved items to', storageKey, '- count:', this.items.length);
      } catch (e) {
        console.error('Could not save projekt items', e);
      }
    },

    // Add item to projekt (or increase quantity if already exists)
    addItem(productId, variantId, title, price, comparePrice, image, url, variantTitle) {
      // Check if variant already exists
      const existingIndex = this.items.findIndex(item => item.variantId === variantId);

      if (existingIndex === -1) {
        // Add new item
        this.items.push({
          productId: productId,
          variantId: variantId,
          title: title,
          variantTitle: variantTitle || '',
          price: price,
          comparePrice: comparePrice || '',
          image: image,
          url: url,
          quantity: 1,
          note: '',
          addedAt: Date.now()
        });
        this.showNotification(getString('notifications.added'));
      } else {
        // Increase quantity of existing item
        this.items[existingIndex].quantity += 1;
        this.showNotification(getString('notifications.quantity_increased'));
      }
      this.saveItems();
      this.updateUI();
      return true;
    },

    // Add custom item to projekt (admin only, for draft orders)
    addCustomItem(title, price, description, style, finish, color, size) {
      const customId = 'custom-' + Date.now();

      // Format price with currency
      const formattedPrice = parseFloat(price).toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }) + ' €';

      this.items.push({
        productId: customId,
        variantId: customId,
        title: title,
        variantTitle: description || '',
        price: formattedPrice,
        rawPrice: parseFloat(price), // Store raw price for draft order
        comparePrice: '',
        image: '',
        url: '',
        quantity: 1,
        isCustom: true,
        customStyle: style || '',
        customFinish: finish || '',
        customColor: color || '',
        customSize: size || '',
        addedAt: Date.now()
      });

      this.saveItems();
      this.updateUI();
      this.showNotification(getString('notifications.custom_added'));
      return true;
    },

    // Edit custom item - opens a popup with pre-filled form
    editCustomItem(variantId) {
      const item = this.items.find(i => i.variantId === variantId);
      if (!item || !item.isCustom) return;

      const popupHtml = `
        <div class="nf-projekt-popup-overlay" id="nf-projekt-edit-popup">
          <div class="nf-projekt-popup">
            <div class="nf-projekt-popup-header">
              <h3 class="nf-projekt-popup-title">${getString('custom.edit_title') || 'Artikel bearbeiten'}</h3>
              <button type="button" class="nf-projekt-popup-close" aria-label="${getString('aria.close')}">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div class="nf-projekt-popup-body">
              <form id="nf-projekt-edit-form">
                <input type="hidden" id="nf-edit-variant-id" value="${item.variantId}">
                <div class="nf-projekt-form-group">
                  <label class="nf-projekt-form-label" for="nf-edit-title">${getString('custom.title_label') || 'Titel'}</label>
                  <input type="text" id="nf-edit-title" class="nf-projekt-form-input" required value="${(item.title || '').replace(/"/g, '&quot;')}">
                </div>
                <div class="nf-projekt-form-group">
                  <label class="nf-projekt-form-label" for="nf-edit-price">${getString('custom.price_label') || 'Preis (€)'}</label>
                  <input type="number" id="nf-edit-price" class="nf-projekt-form-input" required step="0.01" min="0" value="${item.rawPrice || parseFloat(item.price.replace(/[^\\d,.-]/g, '').replace(',', '.')) || ''}">
                </div>
                <div class="nf-projekt-form-group">
                  <label class="nf-projekt-form-label" for="nf-edit-description">${getString('custom.description_label') || 'Beschreibung'}</label>
                  <textarea id="nf-edit-description" class="nf-projekt-form-input" rows="2">${(item.variantTitle || '').replace(/</g, '&lt;')}</textarea>
                </div>
                <div class="nf-projekt-form-row">
                  <div class="nf-projekt-form-group nf-projekt-form-half">
                    <label class="nf-projekt-form-label" for="nf-edit-style">${getString('custom.style_label') || 'Stil'}</label>
                    <input type="text" id="nf-edit-style" class="nf-projekt-form-input" value="${(item.customStyle || '').replace(/"/g, '&quot;')}">
                  </div>
                  <div class="nf-projekt-form-group nf-projekt-form-half">
                    <label class="nf-projekt-form-label" for="nf-edit-finish">${getString('custom.finish_label') || 'Oberfläche'}</label>
                    <input type="text" id="nf-edit-finish" class="nf-projekt-form-input" value="${(item.customFinish || '').replace(/"/g, '&quot;')}">
                  </div>
                </div>
                <div class="nf-projekt-form-row">
                  <div class="nf-projekt-form-group nf-projekt-form-half">
                    <label class="nf-projekt-form-label" for="nf-edit-color">${getString('custom.color_label') || 'Farbe'}</label>
                    <input type="text" id="nf-edit-color" class="nf-projekt-form-input" value="${(item.customColor || '').replace(/"/g, '&quot;')}">
                  </div>
                  <div class="nf-projekt-form-group nf-projekt-form-half">
                    <label class="nf-projekt-form-label" for="nf-edit-size">${getString('custom.size_label') || 'Größe'}</label>
                    <input type="text" id="nf-edit-size" class="nf-projekt-form-input" value="${(item.customSize || '').replace(/"/g, '&quot;')}">
                  </div>
                </div>
                <div class="nf-projekt-custom-form-actions">
                  <button type="submit" class="nf-projekt-btn nf-projekt-custom-submit">${getString('custom.save_button') || 'Speichern'}</button>
                  <button type="button" class="nf-projekt-btn nf-projekt-custom-cancel nf-projekt-edit-cancel">${getString('custom.cancel_button') || 'Abbrechen'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      `;

      document.body.insertAdjacentHTML('beforeend', popupHtml);
      const popup = document.getElementById('nf-projekt-edit-popup');

      requestAnimationFrame(() => {
        popup.classList.add('is-active');
      });

      // Close handlers
      popup.querySelector('.nf-projekt-popup-close').addEventListener('click', () => this.closePopup(popup));
      popup.querySelector('.nf-projekt-edit-cancel').addEventListener('click', () => this.closePopup(popup));
      popup.addEventListener('click', (e) => {
        if (e.target === popup) this.closePopup(popup);
      });

      // Save handler
      popup.querySelector('#nf-projekt-edit-form').addEventListener('submit', (e) => {
        e.preventDefault();
        const editVariantId = popup.querySelector('#nf-edit-variant-id').value;
        const newTitle = popup.querySelector('#nf-edit-title').value.trim();
        const newPrice = parseFloat(popup.querySelector('#nf-edit-price').value);
        const newDescription = popup.querySelector('#nf-edit-description').value.trim();
        const newStyle = popup.querySelector('#nf-edit-style').value.trim();
        const newFinish = popup.querySelector('#nf-edit-finish').value.trim();
        const newColor = popup.querySelector('#nf-edit-color').value.trim();
        const newSize = popup.querySelector('#nf-edit-size').value.trim();

        if (newTitle && !isNaN(newPrice)) {
          this.updateCustomItem(editVariantId, newTitle, newPrice, newDescription, newStyle, newFinish, newColor, newSize);
          this.closePopup(popup);
        }
      });
    },

    // Update an existing custom item
    updateCustomItem(variantId, title, price, description, style, finish, color, size) {
      const item = this.items.find(i => i.variantId === variantId);
      if (!item || !item.isCustom) return;

      item.title = title;
      item.rawPrice = price;
      item.price = price.toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }) + ' €';
      item.variantTitle = description;
      item.customStyle = style;
      item.customFinish = finish;
      item.customColor = color;
      item.customSize = size;

      this.saveItems();
      this.updateUI();
      this.showNotification(getString('notifications.custom_updated') || 'Artikel aktualisiert');
    },

    // Remove item from projekt
    removeItem(variantId) {
      this.items = this.items.filter(item => item.variantId !== variantId);
      this.saveItems();
      this.updateUI();
      this.updateAllHeartButtons();
    },

    // Update item note
    updateItemNote(variantId, note) {
      const item = this.items.find(item => item.variantId === variantId);
      if (item) {
        item.note = note;
        this.saveItems();
      }
    },

    // Update item quantity
    updateQuantity(variantId, quantity) {
      const item = this.items.find(item => item.variantId === variantId);
      if (item) {
        item.quantity = Math.max(1, quantity);
        this.saveItems();
        this.updateUI();
      }
    },

    // Clear all items
    clearAll() {
      this.items = [];
      this.saveItems();
      this.updateUI();
      this.updateAllHeartButtons();
    },

    // Check if variant is in projekt
    isInProjekt(variantId) {
      return this.items.some(item => item.variantId === variantId);
    },

    // Toggle item in projekt
    toggleItem(productId, variantId, title, price, image, url, variantTitle) {
      if (this.isInProjekt(variantId)) {
        this.removeItem(variantId);
        return false;
      } else {
        this.addItem(productId, variantId, title, price, image, url, variantTitle);
        return true;
      }
    },

    // Get total items count
    getCount() {
      return this.items.reduce((sum, item) => sum + item.quantity, 0);
    },

    // Update all UI elements
    updateUI() {
      this.updateSidebar();
      this.updateMobileBar();
      this.updateAllHeartButtons();
    },

    // Update heart button states (no visual change - button always looks the same)
    updateAllHeartButtons() {
      // Buttons remain unchanged regardless of item status
    },

    // Update sidebar content
    updateSidebar() {
      const sidebar = document.querySelector('.nf-projekt-sidebar');
      if (!sidebar) return;

      const itemsList = sidebar.querySelector('.nf-projekt-items');
      const emptyState = sidebar.querySelector('.nf-projekt-empty');
      const footer = sidebar.querySelector('.nf-projekt-footer');
      const countBadge = sidebar.querySelector('.nf-projekt-count');

      if (countBadge) {
        countBadge.textContent = this.getCount();
        countBadge.style.display = this.items.length > 0 ? 'flex' : 'none';
      }

      if (this.items.length === 0) {
        if (itemsList) itemsList.innerHTML = '';
        if (emptyState) emptyState.style.display = 'flex';
        if (footer) footer.style.display = 'none';
        return;
      }

      if (emptyState) emptyState.style.display = 'none';
      if (footer) footer.style.display = 'block';

      if (itemsList) {
        itemsList.innerHTML = this.items.map(item => `
          <div class="nf-projekt-item${item.isCustom ? ' is-custom' : ''}" data-variant-id="${item.variantId}">
            <div class="nf-projekt-item-image">
              ${item.image ? `<img src="${item.image}" alt="${item.title}">` : `<div class="nf-projekt-item-placeholder">${item.isCustom ? '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line></svg>' : ''}</div>`}
            </div>
            <div class="nf-projekt-item-info">
              ${item.isCustom ? `<span class="nf-projekt-item-badge">${getString('labels.custom_badge')}</span>` : ''}
              ${item.url ? `<a href="${item.url}" class="nf-projekt-item-title">${item.title}</a>` : `<span class="nf-projekt-item-title">${item.title}</span>`}
              ${item.variantTitle ? `<span class="nf-projekt-item-variant">${item.variantTitle}</span>` : ''}
              ${item.isCustom && [item.customStyle, item.customFinish, item.customColor, item.customSize].some(v => v) ? `<span class="nf-projekt-item-props">${[
                item.customStyle ? getString('custom.style_label') + ': ' + item.customStyle : '',
                item.customFinish ? getString('custom.finish_label') + ': ' + item.customFinish : '',
                item.customColor ? getString('custom.color_label') + ': ' + item.customColor : '',
                item.customSize ? getString('custom.size_label') + ': ' + item.customSize : ''
              ].filter(Boolean).join(' | ')}</span>` : ''}
              <div class="nf-projekt-item-qty">
                <button type="button" class="nf-projekt-qty-btn" data-action="decrease" data-variant-id="${item.variantId}">-</button>
                <span class="nf-projekt-qty-value">${item.quantity}</span>
                <button type="button" class="nf-projekt-qty-btn" data-action="increase" data-variant-id="${item.variantId}">+</button>
              </div>
              ${document.querySelector('#nf-projekt-sidebar')?.dataset.admin === 'true' ? `<div class="nf-projekt-item-note-wrapper">
                <input type="text" class="nf-projekt-item-note" data-variant-id="${item.variantId}"
                  placeholder="${getString('labels.item_note_placeholder')}"
                  value="${(item.note || '').replace(/"/g, '&quot;')}">
              </div>` : ''}
            </div>
            <div class="nf-projekt-item-price-col">
              ${item.comparePrice ? `<span class="nf-projekt-item-compare-price">${item.comparePrice}</span>` : ''}
              <span class="nf-projekt-item-price">${item.price}</span>
            </div>
            <div class="nf-projekt-item-actions">
              ${item.isCustom ? `<button type="button" class="nf-projekt-item-edit" data-variant-id="${item.variantId}" aria-label="${getString('aria.edit') || 'Bearbeiten'}">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
              </button>` : ''}
              <button type="button" class="nf-projekt-item-remove" data-variant-id="${item.variantId}" aria-label="${getString('aria.remove')}">
                <i class="icon-close"></i>
              </button>
            </div>
          </div>
        `).join('');
      }

      // Update total price
      this.updateTotalPrice();
    },

    // Calculate and update total price (with discount support)
    updateTotalPrice() {
      const footer = document.querySelector('.nf-projekt-footer');
      if (!footer) return;

      let totalEl = footer.querySelector('.nf-projekt-total');

      if (this.items.length === 0) {
        if (totalEl) totalEl.remove();
        return;
      }

      // Calculate subtotal from stored price values
      let subtotal = 0;
      this.items.forEach(item => {
        const priceStr = item.price.replace(/[^\d,.-]/g, '').replace(',', '.');
        const price = parseFloat(priceStr) || 0;
        subtotal += price * item.quantity;
      });

      // Read discount values (admin only)
      const discountInput = document.getElementById('nf-projekt-discount-value');
      const discountTypeSelect = document.getElementById('nf-projekt-discount-type');
      const discountValue = parseFloat(discountInput?.value) || 0;
      const discountType = discountTypeSelect?.value || 'percentage';

      let discountAmount = 0;
      if (discountValue > 0) {
        if (discountType === 'percentage') {
          discountAmount = subtotal * (discountValue / 100);
        } else {
          discountAmount = discountValue;
        }
      }

      // Read second discount values (admin only)
      const discount2Input = document.getElementById('nf-projekt-discount2-value');
      const discount2TypeSelect = document.getElementById('nf-projekt-discount2-type');
      const discount2Value = parseFloat(discount2Input?.value) || 0;
      const discount2Type = discount2TypeSelect?.value || 'percentage';

      let discount2Amount = 0;
      if (discount2Value > 0) {
        if (discount2Type === 'percentage') {
          discount2Amount = subtotal * (discount2Value / 100);
        } else {
          discount2Amount = discount2Value;
        }
      }

      const totalDiscountAmount = discountAmount + discount2Amount;

      // Read shipping cost (admin only)
      const shippingSelect = document.getElementById('nf-projekt-shipping-select');
      const shippingCustomInput = document.getElementById('nf-projekt-shipping-custom');
      let shippingCost = 0;
      let shippingIsDefault = false;
      if (shippingSelect) {
        if (shippingSelect.value === 'default') {
          shippingIsDefault = true;
          shippingCost = 0;
        } else {
          shippingCost = shippingSelect.value === 'custom'
            ? (parseFloat(shippingCustomInput?.value) || 0)
            : (parseFloat(shippingSelect.value) || 0);
        }
      }

      const finalTotal = Math.max(0, subtotal - totalDiscountAmount + shippingCost);

      const formatEur = (val) => val.toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }) + ' €';

      if (!totalEl) {
        totalEl = document.createElement('div');
        totalEl.className = 'nf-projekt-total';
        const actionsEl = footer.querySelector('.nf-projekt-actions');
        if (actionsEl) {
          footer.insertBefore(totalEl, actionsEl);
        } else {
          footer.prepend(totalEl);
        }
      }

      if (totalDiscountAmount > 0 || shippingCost > 0) {
        const discountLabel = discountType === 'percentage'
          ? `${getString('labels.discount_label') || 'Rabatt'} (${discountValue}%)`
          : `${getString('labels.discount_label') || 'Rabatt'}`;
        const discount2Label = discount2Type === 'percentage'
          ? `${getString('labels.discount_label2') || 'Rabatt 2'} (${discount2Value}%)`
          : `${getString('labels.discount_label2') || 'Rabatt 2'}`;
        let rows = `
          <div class="nf-projekt-total-row">
            <span class="nf-projekt-total-label">${getString('labels.subtotal_label') || 'Zwischensumme'}</span>
            <span class="nf-projekt-total-value">${formatEur(subtotal)}</span>
          </div>`;
        if (discountAmount > 0) {
          rows += `
          <div class="nf-projekt-total-row nf-projekt-discount-row">
            <span class="nf-projekt-total-label">${discountLabel}</span>
            <span class="nf-projekt-total-value nf-projekt-discount-value">-${formatEur(discountAmount)}</span>
          </div>`;
        }
        if (discount2Amount > 0) {
          rows += `
          <div class="nf-projekt-total-row nf-projekt-discount-row">
            <span class="nf-projekt-total-label">${discount2Label}</span>
            <span class="nf-projekt-total-value nf-projekt-discount-value">-${formatEur(discount2Amount)}</span>
          </div>`;
        }
        if (shippingIsDefault) {
          rows += `
          <div class="nf-projekt-total-row">
            <span class="nf-projekt-total-label">Versand</span>
            <span class="nf-projekt-total-value" style="font-size:0.85em;color:var(--projekt-text-light,#888)">Im Checkout berechnet</span>
          </div>`;
        } else if (shippingCost > 0) {
          rows += `
          <div class="nf-projekt-total-row">
            <span class="nf-projekt-total-label">Versand</span>
            <span class="nf-projekt-total-value">+${formatEur(shippingCost)}</span>
          </div>`;
        }
        rows += `
          <div class="nf-projekt-total-row nf-projekt-final-row">
            <span class="nf-projekt-total-label">${getString('labels.total_label') || 'Gesamt'}</span>
            <span class="nf-projekt-total-value">${formatEur(finalTotal)}</span>
          </div>`;
        totalEl.innerHTML = rows;
      } else {
        totalEl.innerHTML = `
          <span class="nf-projekt-total-label">${getString('labels.total_label') || 'Gesamt'}</span>
          <span class="nf-projekt-total-value">${formatEur(subtotal)}</span>
        `;
      }
    },

    // Update mobile bar
    updateMobileBar() {
      const mobileBar = document.querySelector('.nf-projekt-mobile-bar');
      if (!mobileBar) return;

      const countBadge = mobileBar.querySelector('.nf-projekt-mobile-count');
      if (countBadge) {
        const count = this.getCount();
        countBadge.textContent = count;
        countBadge.style.display = count > 0 ? 'flex' : 'none';
      }

      // Show/hide mobile bar based on items
      if (this.items.length > 0) {
        mobileBar.classList.add('has-items');
      } else {
        mobileBar.classList.remove('has-items');
      }
    },

    // Generate shareable URL
    generateShareUrl() {
      const itemsData = this.items.map(item => ({
        v: item.variantId,
        q: item.quantity
      }));
      const encoded = btoa(JSON.stringify(itemsData));
      const url = new URL(window.location.href);
      url.searchParams.set('projekt', encoded);
      return url.toString();
    },

    // Load items from URL
    loadFromUrl() {
      const url = new URL(window.location.href);
      const projektParam = url.searchParams.get('projekt');

      if (projektParam) {
        try {
          const decoded = JSON.parse(atob(projektParam));
          if (Array.isArray(decoded)) {
            this.loadSharedItems(decoded);
          }
        } catch (e) {
          console.error('Could not load shared projekt', e);
        }
      }
    },

    // Load shared items (fetch product data)
    async loadSharedItems(itemsData) {
      for (const item of itemsData) {
        const heartBtn = document.querySelector(`.nf-projekt-btn-add[data-variant-id="${item.v}"]`);
        if (heartBtn) {
          const productId = heartBtn.dataset.productId;
          const title = heartBtn.dataset.productTitle;
          const price = heartBtn.dataset.productPrice;
          const image = heartBtn.dataset.productImage;
          const url = heartBtn.dataset.productUrl;
          const variantTitle = heartBtn.dataset.variantTitle;

          if (!this.isInProjekt(item.v)) {
            this.items.push({
              productId: productId,
              variantId: item.v,
              title: title,
              variantTitle: variantTitle || '',
              price: price,
              image: image,
              url: url,
              quantity: item.q || 1,
              addedAt: Date.now()
            });
          }
        }
      }
      this.saveItems();
      this.updateUI();
    },

    // Open share popup
    openSharePopup() {
      const url = this.generateShareUrl();
      const itemsList = this.items.map(item =>
        `<li><strong>${item.title}</strong>${item.variantTitle ? ' <span style="color:#666;">(' + item.variantTitle + ')</span>' : ''} <span style="color:#666;">× ${item.quantity}</span></li>`
      ).join('');

      const popupHtml = `
        <div class="nf-projekt-popup-overlay" id="nf-projekt-share-popup">
          <div class="nf-projekt-popup">
            <div class="nf-projekt-popup-header">
              <h3 class="nf-projekt-popup-title">${getString('share.title')}</h3>
              <button type="button" class="nf-projekt-popup-close" aria-label="${getString('aria.close')}">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div class="nf-projekt-popup-body">
              <div class="nf-projekt-items-summary">
                <div class="nf-projekt-items-summary-title">${formatString(getString('share.summary_title'), { count: this.getCount() })}</div>
                <ul class="nf-projekt-items-summary-list">${itemsList}</ul>
              </div>
              <div class="nf-projekt-share-actions">
                <button type="button" class="nf-projekt-share-btn primary" data-action="copy-url">
                  <i class="icon-link"></i>
                  ${getString('share.copy_button')}
                </button>
                <button type="button" class="nf-projekt-share-btn secondary" data-action="email-self">
                  <i class="icon-mail"></i>
                  ${getString('share.email_button')}
                </button>
              </div>
            </div>
          </div>
        </div>
      `;

      document.body.insertAdjacentHTML('beforeend', popupHtml);
      const popup = document.getElementById('nf-projekt-share-popup');

      requestAnimationFrame(() => {
        popup.classList.add('is-active');
      });

      // Bind popup events
      popup.querySelector('.nf-projekt-popup-close').addEventListener('click', () => this.closePopup(popup));
      popup.addEventListener('click', (e) => {
        if (e.target === popup) this.closePopup(popup);
      });

      popup.querySelector('[data-action="copy-url"]').addEventListener('click', async () => {
        await this.copyToClipboard(url);
        this.showNotification(getString('notifications.link_copied'));
      });

      popup.querySelector('[data-action="email-self"]').addEventListener('click', () => {
        this.openEmailSelfPopup(url);
        this.closePopup(popup);
      });
    },

    // Open email to self popup
    openEmailSelfPopup(projektUrl) {
      const popupHtml = `
        <div class="nf-projekt-popup-overlay" id="nf-projekt-email-popup">
          <div class="nf-projekt-popup">
            <div class="nf-projekt-popup-header">
              <h3 class="nf-projekt-popup-title">${getString('share.email_popup_title')}</h3>
              <button type="button" class="nf-projekt-popup-close" aria-label="${getString('aria.close')}">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div class="nf-projekt-popup-body">
              <form id="nf-projekt-email-form">
                <div class="nf-projekt-form-group">
                  <label class="nf-projekt-form-label" for="nf-projekt-email">${getString('share.email_label')}</label>
                  <input type="email" id="nf-projekt-email" class="nf-projekt-form-input" required placeholder="${getString('share.email_placeholder')}">
                </div>
                <div class="nf-projekt-form-group">
                  <label class="nf-projekt-form-label" for="nf-projekt-name">${getString('share.name_label')}</label>
                  <input type="text" id="nf-projekt-name" class="nf-projekt-form-input" placeholder="${getString('share.name_placeholder')}">
                </div>
                <input type="hidden" id="nf-projekt-url" value="${projektUrl}">
                <button type="submit" class="nf-projekt-share-btn primary" style="width: 100%; margin-top: 10px;">
                  ${getString('share.send_button')}
                </button>
              </form>
            </div>
          </div>
        </div>
      `;

      document.body.insertAdjacentHTML('beforeend', popupHtml);
      const popup = document.getElementById('nf-projekt-email-popup');

      requestAnimationFrame(() => {
        popup.classList.add('is-active');
      });

      popup.querySelector('.nf-projekt-popup-close').addEventListener('click', () => this.closePopup(popup));
      popup.addEventListener('click', (e) => {
        if (e.target === popup) this.closePopup(popup);
      });

      popup.querySelector('#nf-projekt-email-form').addEventListener('submit', (e) => {
        e.preventDefault();
        const email = popup.querySelector('#nf-projekt-email').value;
        const name = popup.querySelector('#nf-projekt-name').value;
        const url = popup.querySelector('#nf-projekt-url').value;

        // Open mailto link
        const subject = encodeURIComponent(getString('share.mail_subject'));
        const greeting = name ? formatString(getString('share.mail_greeting'), { name }) + '\n\n' : '';
        const bodyText = formatString(getString('share.mail_body'), { url });
        const closing = getString('share.mail_closing');
        const body = encodeURIComponent(`${greeting}${bodyText}${closing ? '\n\n' + closing : ''}`);
        window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;

        this.closePopup(popup);
        this.showNotification(getString('notifications.email_opening'));
      });
    },

    // Load EmailJS SDK
    async loadEmailJS() {
      if (window.emailjs) return window.emailjs;

      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/@emailjs/browser@3/dist/email.min.js';
        script.onload = () => {
          window.emailjs.init('_S8vJfwcGPhiB044N');
          resolve(window.emailjs);
        };
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },

    // Open contact popup
    openContactPopup() {
      const url = this.generateShareUrl();
      const itemsList = this.items.map(item =>
        `<li>${item.title}${item.variantTitle ? ' (' + item.variantTitle + ')' : ''} x ${item.quantity} - ${item.price}</li>`
      ).join('');

      const itemsText = this.items.map(item =>
        `- ${item.title}${item.variantTitle ? ' (' + item.variantTitle + ')' : ''} x ${item.quantity} - ${item.price}`
      ).join('\n');

      const popupHtml = `
        <div class="nf-projekt-popup-overlay" id="nf-projekt-contact-popup">
          <div class="nf-projekt-popup">
            <div class="nf-projekt-popup-header">
              <h3 class="nf-projekt-popup-title">${getString('contact.title')}</h3>
              <button type="button" class="nf-projekt-popup-close" aria-label="${getString('aria.close')}">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div class="nf-projekt-popup-body">
              <div class="nf-projekt-items-summary" style="margin-bottom: 20px;">
                <div class="nf-projekt-items-summary-title">${formatString(getString('contact.summary_title'), { count: this.getCount() })}</div>
                <ul class="nf-projekt-items-summary-list">${itemsList}</ul>
              </div>
              <form id="nf-projekt-contact-form">
                <div class="nf-projekt-form-group">
                  <label class="nf-projekt-form-label" for="nf-contact-name">${getString('contact.name_label')}</label>
                  <input type="text" id="nf-contact-name" class="nf-projekt-form-input" required placeholder="${getString('contact.name_placeholder')}">
                </div>
                <div class="nf-projekt-form-group">
                  <label class="nf-projekt-form-label" for="nf-contact-email">${getString('contact.email_label')}</label>
                  <input type="email" id="nf-contact-email" class="nf-projekt-form-input" required placeholder="${getString('contact.email_placeholder')}">
                </div>
                <div class="nf-projekt-form-group">
                  <label class="nf-projekt-form-label" for="nf-contact-phone">${getString('contact.phone_label')}</label>
                  <input type="tel" id="nf-contact-phone" class="nf-projekt-form-input" placeholder="${getString('contact.phone_placeholder')}">
                </div>
                <div class="nf-projekt-form-group">
                  <label class="nf-projekt-form-label" for="nf-contact-message">${getString('contact.message_label')}</label>
                  <textarea id="nf-contact-message" class="nf-projekt-form-textarea" placeholder="${getString('contact.message_placeholder')}"></textarea>
                </div>
                <input type="hidden" id="nf-contact-projekt-url" value="${url}">
                <input type="hidden" id="nf-contact-items-text" value="${itemsText.replace(/"/g, '&quot;')}">
                <button type="submit" class="nf-projekt-share-btn primary" style="width: 100%;">
                  ${getString('contact.send_button')}
                </button>
              </form>
              <p class="nf-projekt-form-note">${getString('contact.note')}</p>
            </div>
          </div>
        </div>
      `;

      document.body.insertAdjacentHTML('beforeend', popupHtml);
      const popup = document.getElementById('nf-projekt-contact-popup');

      requestAnimationFrame(() => {
        popup.classList.add('is-active');
      });

      popup.querySelector('.nf-projekt-popup-close').addEventListener('click', () => this.closePopup(popup));
      popup.addEventListener('click', (e) => {
        if (e.target === popup) this.closePopup(popup);
      });

      // Form submission with EmailJS
      popup.querySelector('#nf-projekt-contact-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const submitBtn = popup.querySelector('button[type="submit"]');
        const originalBtnText = submitBtn.innerHTML;

        const name = popup.querySelector('#nf-contact-name').value;
        const email = popup.querySelector('#nf-contact-email').value;
        const phone = popup.querySelector('#nf-contact-phone').value || 'Nicht angegeben';
        const message = popup.querySelector('#nf-contact-message').value || 'Keine zusätzliche Nachricht';
        const projektUrl = popup.querySelector('#nf-contact-projekt-url').value;
        const itemsText = popup.querySelector('#nf-contact-items-text').value;

        submitBtn.disabled = true;
        submitBtn.innerHTML = '<span class="spinner"></span> ' + (getString('contact.sending') || 'Wird gesendet...');

        try {
          // Load EmailJS if not loaded
          await this.loadEmailJS();

          // Generate unique request ID (timestamp + random)
          const requestId = 'NF-' + Date.now().toString(36).toUpperCase() + Math.random().toString(36).substring(2, 6).toUpperCase();

          // Send email via EmailJS
          await window.emailjs.send('service_4e0wwfn', 'template_l98z1fn', {
            request_id: requestId,
            from_name: name,
            from_email: email,
            phone: phone,
            message: message,
            projekt_items: itemsText,
            projekt_url: projektUrl
          });

          // Show success message
          popup.querySelector('.nf-projekt-popup-body').innerHTML = `
            <div class="nf-projekt-success-message" style="text-align: center; padding: 40px 20px;">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#4CAF50" stroke-width="2" style="margin-bottom: 20px;">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
              <h3 style="margin: 0 0 10px; font-size: 1.3em;">${getString('contact.success_title') || 'Anfrage gesendet!'}</h3>
              <p style="margin: 0; color: #666;">${getString('contact.success_message') || 'Wir werden uns so schnell wie möglich bei Ihnen melden.'}</p>
            </div>
          `;

          // Auto close after 3 seconds
          setTimeout(() => this.closePopup(popup), 3000);

        } catch (error) {
          console.error('EmailJS error:', error);
          submitBtn.disabled = false;
          submitBtn.innerHTML = originalBtnText;
          this.showNotification(getString('contact.error_message') || 'Fehler beim Senden. Bitte versuchen Sie es erneut.', 'error');
        }
      });
    },

    // Close popup
    closePopup(popup) {
      popup.classList.remove('is-active');
      setTimeout(() => popup.remove(), 300);
    },

    // Copy to clipboard
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (e) {
        // Fallback for older browsers
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        return true;
      }
    },

    // Generate PDF of the offer
    async generatePDF(draftOrderId, totalPrice, invoiceUrl, customerData, agentName, notes, returnBase64 = false, draftOrderName = null) {
      // Load jsPDF if not already loaded
      if (!window.jspdf) {
        const jspdfUrl = ASSETS.jspdf;
        if (!jspdfUrl) {
          throw new Error(getString('notifications.pdf_library_missing'));
        }
        await this.loadScript(jspdfUrl);
      }

      const { jsPDF } = window.jspdf;
      const doc = new jsPDF();

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - margin * 2;
      const brandPrimary = [87, 92, 81];
      const brandAccent = [255, 112, 77];
      const brandDark = [14, 14, 14];
      const brandGray = [100, 100, 100];
      const colItem = margin + 3;
      const colQty = pageWidth - 55;
      const colPrice = pageWidth - margin - 3;
      let yPos = 0;

      // Reserve space for footer (contact info + trust logos + bar)
      const footerHeight = 55;

      const checkPageBreak = (needed) => {
        if (yPos + needed > pageHeight - footerHeight) {
          doc.addPage();
          yPos = 20;
        }
      };

      // ── Header: logo left, "Angebot" right ──
      let logoLoaded = false;
      try {
        const logoUrl = 'https://novafronten.de/cdn/shop/files/NF_-_Logo_v2_34d2884a-534e-471a-ab5b-786f883c16f6.svg?v=1731052081&width=460';
        const logoImg = await this.loadImage(logoUrl);
        const tmpImg = new Image();
        const logoDims = await new Promise((resolve) => {
          tmpImg.onload = () => resolve({ w: tmpImg.naturalWidth, h: tmpImg.naturalHeight });
          tmpImg.src = logoImg;
        });
        // Target ~55mm width (roughly 200px at 96dpi), auto height
        const logoTargetW = 55;
        const logoH = (logoDims.h / logoDims.w) * logoTargetW;
        doc.addImage(logoImg, 'PNG', margin, 10, logoTargetW, logoH);
        logoLoaded = true;
      } catch (e) {
        console.log('Could not load logo for PDF');
      }

      if (!logoLoaded) {
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...brandDark);
        doc.text('NOVAFRONTEN', margin, 18);
      }

      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...brandPrimary);
      doc.text(getString('pdf.title'), pageWidth - margin, 20, { align: 'right' });

      // Thin line under header
      doc.setDrawColor(...brandPrimary);
      doc.setLineWidth(0.5);
      doc.line(margin, 28, pageWidth - margin, 28);

      yPos = 35;

      // ── Date & offer number ──
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...brandGray);
      const today = new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
      doc.text(formatString(getString('pdf.date_label'), { date: today }), margin, yPos);
      if (draftOrderId) {
        doc.text(formatString(getString('pdf.offer_number_label'), { id: draftOrderName || draftOrderId }), pageWidth - margin, yPos, { align: 'right' });
      }
      yPos += 10;

      // ── Customer info ──
      if (customerData) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...brandPrimary);
        doc.text(getString('pdf.customer_label'), margin, yPos);
        yPos += 5;
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...brandDark);
        if (customerData.name) { doc.text(customerData.name, margin, yPos); yPos += 4.5; }
        if (customerData.email) { doc.text(customerData.email, margin, yPos); yPos += 4.5; }
        if (customerData.address) { doc.text(customerData.address, margin, yPos); yPos += 4.5; }
        if (customerData.zipCity) { doc.text(customerData.zipCity, margin, yPos); yPos += 4.5; }
        yPos += 2;
      }

      if (agentName) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...brandPrimary);
        const agentLabel = getString('pdf.agent_label');
        doc.text(agentLabel, margin, yPos);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...brandDark);
        doc.text(' ' + agentName, margin + doc.getTextWidth(agentLabel + ' '), yPos);
        yPos += 7;
      }

      // ── Notes section ──
      if (notes) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...brandPrimary);
        doc.text(getString('pdf.notes_label'), margin, yPos);
        yPos += 5;
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...brandDark);
        const notesLines = doc.splitTextToSize(notes, contentWidth - 6);
        notesLines.forEach(line => {
          checkPageBreak(5);
          doc.text(line, margin + 3, yPos);
          yPos += 4.5;
        });
        yPos += 4;
      }

      // ── Table header row ──
      yPos += 2;
      const headerH = 8;
      doc.setFillColor(...brandPrimary);
      doc.rect(margin, yPos, contentWidth, headerH, 'F');
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      const headerTextY = yPos + 5.5;
      doc.text(getString('pdf.items_label'), colItem, headerTextY);
      doc.text(getString('pdf.quantity_label'), colQty, headerTextY);
      doc.text(getString('pdf.price_label'), colPrice, headerTextY, { align: 'right' });
      yPos += headerH;

      // ── Table rows ──
      let isAlternate = false;
      const titleMaxWidth = colQty - colItem - 5;

      this.items.forEach(item => {
        const pad = 3;

        // Pre-compute all text and height
        doc.setFontSize(9);
        const titleLines = doc.splitTextToSize(item.title, titleMaxWidth);
        let textH = titleLines.length * 4;

        doc.setFontSize(7.5);
        let variantText = '';
        let descLines = [];
        let noteLines = [];
        if (item.variantTitle && !item.isCustom) {
          variantText = item.variantTitle;
          textH += 4;
        }
        // Per-item note
        if (item.note) {
          noteLines = doc.splitTextToSize(getString('labels.item_note_label') + ': ' + item.note, titleMaxWidth - 6);
          textH += noteLines.length * 3.5;
        }
        let propsLine = '';
        if (item.isCustom) {
          textH += 4;
          // Build properties line
          const props = [
            item.customStyle ? getString('custom.style_label') + ': ' + item.customStyle : '',
            item.customFinish ? getString('custom.finish_label') + ': ' + item.customFinish : '',
            item.customColor ? getString('custom.color_label') + ': ' + item.customColor : '',
            item.customSize ? getString('custom.size_label') + ': ' + item.customSize : ''
          ].filter(Boolean);
          if (props.length > 0) {
            propsLine = props.join(' | ');
            textH += 4;
          }
          if (item.variantTitle) {
            descLines = doc.splitTextToSize(item.variantTitle, titleMaxWidth - 6);
            textH += descLines.length * 3.5;
          }
        }

        const rowH = pad + textH + pad;
        checkPageBreak(rowH);

        // Row background
        if (isAlternate) {
          doc.setFillColor(244, 245, 243);
        } else {
          doc.setFillColor(255, 255, 255);
        }
        doc.rect(margin, yPos, contentWidth, rowH, 'F');

        // Thin bottom border
        doc.setDrawColor(230, 230, 230);
        doc.setLineWidth(0.2);
        doc.line(margin, yPos + rowH, margin + contentWidth, yPos + rowH);

        isAlternate = !isAlternate;

        // Content starts at yPos + pad + first line baseline
        let textY = yPos + pad + 3;

        // Title
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...brandDark);
        titleLines.forEach((line, i) => {
          doc.text(line, colItem, textY + i * 4);
        });

        // Qty + price on first line
        doc.text(item.quantity.toString(), colQty, textY);
        doc.text(item.price, colPrice, textY, { align: 'right' });

        textY += titleLines.length * 4;

        // Variant
        if (variantText) {
          doc.setFontSize(7.5);
          doc.setTextColor(...brandGray);
          doc.text(variantText, colItem + 3, textY);
          textY += 4;
        }

        // Per-item note
        if (noteLines.length > 0) {
          doc.setFontSize(7.5);
          doc.setFont('helvetica', 'italic');
          doc.setTextColor(...brandAccent);
          noteLines.forEach((line, i) => {
            doc.text(line, colItem + 3, textY + i * 3.5);
          });
          textY += noteLines.length * 3.5;
          doc.setFont('helvetica', 'normal');
        }

        // Custom item badge + properties + description
        if (item.isCustom) {
          doc.setFontSize(7.5);
          doc.setTextColor(...brandAccent);
          doc.text(getString('pdf.custom_item_label'), colItem + 3, textY);
          textY += 4;
          if (propsLine) {
            doc.setTextColor(...brandGray);
            doc.text(propsLine, colItem + 3, textY);
            textY += 4;
          }
          if (descLines.length > 0) {
            doc.setTextColor(...brandGray);
            descLines.forEach((line, i) => {
              doc.text(line, colItem + 3, textY + i * 3.5);
            });
          }
        }

        yPos += rowH;
      });

      // ── Calculate subtotal from items ──
      let pdfSubtotal = 0;
      this.items.forEach(item => {
        const rawPrice = item.rawPrice || parseFloat((item.price || '0').replace(/[^\d,.-]/g, '').replace(',', '.'));
        pdfSubtotal += rawPrice * (item.quantity || 1);
      });
      const pdfFormatEur = (val) => val.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' \u20AC';

      // ── Discount rows (if applicable) ──
      const pdfDiscountInput = document.getElementById('nf-projekt-discount-value');
      const pdfDiscountTypeSelect = document.getElementById('nf-projekt-discount-type');
      const pdfDiscountValue = parseFloat(pdfDiscountInput?.value) || 0;
      const pdfDiscountType = pdfDiscountTypeSelect?.value || 'percentage';

      const pdfDiscount2Input = document.getElementById('nf-projekt-discount2-value');
      const pdfDiscount2TypeSelect = document.getElementById('nf-projekt-discount2-type');
      const pdfDiscount2Value = parseFloat(pdfDiscount2Input?.value) || 0;
      const pdfDiscount2Type = pdfDiscount2TypeSelect?.value || 'percentage';

      let pdfDiscountAmount = 0;
      if (pdfDiscountValue > 0) {
        if (pdfDiscountType === 'percentage') {
          pdfDiscountAmount = pdfSubtotal * (pdfDiscountValue / 100);
        } else {
          pdfDiscountAmount = pdfDiscountValue;
        }
      }

      let pdfDiscount2Amount = 0;
      if (pdfDiscount2Value > 0) {
        if (pdfDiscount2Type === 'percentage') {
          pdfDiscount2Amount = pdfSubtotal * (pdfDiscount2Value / 100);
        } else {
          pdfDiscount2Amount = pdfDiscount2Value;
        }
      }

      const pdfTotalDiscount = pdfDiscountAmount + pdfDiscount2Amount;

      if (pdfTotalDiscount > 0) {
        // Subtotal row
        yPos += 1;
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...brandDark);
        doc.text(getString('pdf.subtotal_label') || 'Zwischensumme:', colItem, yPos + 4);
        doc.text(pdfFormatEur(pdfSubtotal), colPrice, yPos + 4, { align: 'right' });
        yPos += 7;

        // Discount 1 row
        if (pdfDiscountAmount > 0) {
          const pdfDiscountLabel = pdfDiscountType === 'percentage'
            ? (getString('pdf.discount_label') || 'Rabatt') + ' (' + pdfDiscountValue + '%)'
            : (getString('pdf.discount_label') || 'Rabatt');
          doc.setTextColor(220, 53, 69);
          doc.text(pdfDiscountLabel, colItem, yPos + 4);
          doc.text('-' + pdfFormatEur(pdfDiscountAmount), colPrice, yPos + 4, { align: 'right' });
          yPos += 7;
        }

        // Discount 2 row
        if (pdfDiscount2Amount > 0) {
          const pdfDiscount2Label = pdfDiscount2Type === 'percentage'
            ? (getString('pdf.discount_label2') || 'Rabatt 2') + ' (' + pdfDiscount2Value + '%)'
            : (getString('pdf.discount_label2') || 'Rabatt 2');
          doc.setTextColor(220, 53, 69);
          doc.text(pdfDiscount2Label, colItem, yPos + 4);
          doc.text('-' + pdfFormatEur(pdfDiscount2Amount), colPrice, yPos + 4, { align: 'right' });
          yPos += 7;
        }
        yPos += 1;
      }

      // ── Shipping row ──
      yPos += 1;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...brandGray);
      doc.text(getString('pdf.shipping_label'), colItem, yPos + 4);
      // Read actual shipping cost from dropdown
      const pdfShippingSelect = document.getElementById('nf-projekt-shipping-select');
      const pdfShippingCustom = document.getElementById('nf-projekt-shipping-custom');
      let pdfShippingCost = 0;
      let pdfShippingIsDefault = false;
      if (pdfShippingSelect) {
        if (pdfShippingSelect.value === 'default') {
          pdfShippingIsDefault = true;
        } else {
          pdfShippingCost = pdfShippingSelect.value === 'custom'
            ? (parseFloat(pdfShippingCustom?.value) || 0)
            : (parseFloat(pdfShippingSelect.value) || 0);
        }
      }
      let pdfShippingText;
      if (pdfShippingIsDefault) {
        pdfShippingText = 'Im Checkout berechnet';
      } else if (pdfShippingCost > 0) {
        pdfShippingText = pdfShippingCost.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' \u20AC';
      } else {
        pdfShippingText = getString('pdf.shipping_free') || 'Kostenlos';
      }
      doc.text(pdfShippingText, colPrice, yPos + 4, { align: 'right' });
      yPos += 8;

      // ── Total bar (calculated: subtotal - discounts + shipping) ──
      const pdfFinalTotal = pdfSubtotal - pdfTotalDiscount + pdfShippingCost;
      const pdfTotalFormatted = pdfFormatEur(pdfFinalTotal);
      const totalBarH = 11;
      doc.setFillColor(...brandPrimary);
      doc.rect(margin, yPos, contentWidth, totalBarH, 'F');
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      const totalTextY = yPos + (totalBarH / 2) + 2;
      doc.text(getString('pdf.total_label'), colItem, totalTextY);
      doc.text(pdfTotalFormatted, colPrice, totalTextY, { align: 'right' });
      yPos += totalBarH + 3;

      // VAT note
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...brandGray);
      doc.text(getString('pdf.vat_note'), colPrice, yPos, { align: 'right' });
      yPos += 8;

      // ── Info box: production time, delivery, validity ──
      checkPageBreak(24);
      doc.setFillColor(244, 245, 243);
      doc.rect(margin, yPos, contentWidth, 20, 'F');
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...brandDark);
      doc.text(getString('pdf.production_time'), margin + 4, yPos + 5);
      doc.text(getString('pdf.delivery_note'), margin + 4, yPos + 10);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(...brandPrimary);
      doc.text(getString('pdf.validity_note'), margin + 4, yPos + 15);
      yPos += 24;

      // ── Important notes section ──
      const importantNotes = [
        'Die Dicke der Deckplatten betr\u00e4gt 18 mm.',
        'Die Fronten sind mit entsprechenden Bohrungen f\u00fcr Scharniere und Schubladen versehen, au\u00dfer bei Elementen, bei denen keine Bohrungen erforderlich sind.',
        'Griffbohrungen sind nicht enthalten.',
        'Scharniere m\u00fcssen separat bei IKEA erworben werden und sind nicht im Lieferumfang der Fronten enthalten.'
      ];
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      // Pre-calculate total height needed
      let notesContentH = 6; // header line height
      const noteLineH = 3.2;
      const wrappedNotes = importantNotes.map(note => {
        const lines = doc.splitTextToSize('\u2022 ' + note, contentWidth - 10);
        notesContentH += lines.length * noteLineH + 1.5;
        return lines;
      });
      const noteBoxH = notesContentH + 4;
      checkPageBreak(noteBoxH);
      yPos += 2;
      doc.setFillColor(255, 252, 240);
      doc.setDrawColor(220, 200, 140);
      doc.setLineWidth(0.3);
      doc.rect(margin, yPos, contentWidth, noteBoxH, 'FD');
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...brandDark);
      doc.text('Wichtige Hinweise:', margin + 4, yPos + 5);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(80, 80, 80);
      let noteY = yPos + 9;
      wrappedNotes.forEach(lines => {
        lines.forEach(line => {
          doc.text(line, margin + 4, noteY);
          noteY += noteLineH;
        });
        noteY += 1.5;
      });
      yPos += noteBoxH + 2;

      // ── Invoice link ──
      if (invoiceUrl) {
        checkPageBreak(14);
        yPos += 2;
        doc.setFillColor(244, 245, 243);
        doc.rect(margin, yPos - 2, contentWidth, 12, 'F');
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...brandPrimary);
        const olLabel = getString('pdf.online_offer_label');
        doc.text(olLabel, margin + 4, yPos + 4);
        doc.setFontSize(8.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 102, 204);
        doc.textWithLink(invoiceUrl, margin + 4 + doc.getTextWidth(olLabel + '  '), yPos + 4, { url: invoiceUrl });
        yPos += 14;
      }

      // ── Footer: helpful info section ──
      const footerStartY = pageHeight - footerHeight;

      // If content goes below footer area, add a new page
      if (yPos > footerStartY - 5) {
        doc.addPage();
      }

      // Separator line
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.3);
      doc.line(margin, footerStartY, pageWidth - margin, footerStartY);

      let fy = footerStartY + 5;

      // Trust logos bar (natural aspect ratio)
      try {
        const trustImg = await this.loadImage('https://cdn.shopify.com/s/files/1/0634/7390/9900/files/685274ac14d4e27331e842cd.png?v=1772095102');
        const trustTmpImg = new Image();
        const trustDims = await new Promise((resolve) => {
          trustTmpImg.onload = () => resolve({ w: trustTmpImg.naturalWidth, h: trustTmpImg.naturalHeight });
          trustTmpImg.src = trustImg;
        });
        const trustMaxW = contentWidth * 0.75;
        const trustW = Math.min(trustMaxW, trustDims.w * 0.264583);
        const trustH = (trustDims.h / trustDims.w) * trustW;
        const trustX = margin + (contentWidth - trustW) / 2;
        doc.addImage(trustImg, 'PNG', trustX, fy, trustW, trustH);
      } catch (e) {
        console.log('Could not load trust logos for PDF');
      }

      // ── Bottom brand bar ──
      doc.setFillColor(...brandPrimary);
      doc.rect(0, pageHeight - 8, pageWidth, 8, 'F');
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(255, 255, 255);
      doc.text(getString('pdf.footer'), pageWidth / 2, pageHeight - 2.5, { align: 'center' });

      // Save or return PDF
      const filePrefix = getString('pdf.file_prefix');
      const fileFallback = getString('pdf.file_fallback_name');
      const fileName = `${filePrefix}_${draftOrderId || fileFallback}_${today.replace(/\./g, '-')}.pdf`;
      if (returnBase64) {
        return doc.output('datauristring');
      }
      doc.save(fileName);
    },

    // Load image for PDF
    loadImage(url) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = reject;
        img.src = url;
      });
    },

    // Load external script dynamically
    loadScript(src) {
      return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.src = src;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },

    // Add all items to cart (only regular items, not custom)
    async addAllToCart() {
      const regularItems = this.items.filter(item => !item.isCustom);
      const customItems = this.items.filter(item => item.isCustom);
      const sidebar = document.getElementById('nf-projekt-sidebar');
      const customVariantId = sidebar?.dataset.customItemVariantId
        ? parseInt(sidebar.dataset.customItemVariantId, 10)
        : null;
      const hasCustomVariant = Number.isInteger(customVariantId) && customVariantId > 0;

      if (regularItems.length === 0 && customItems.length === 0) {
        this.showNotification(getString('notifications.no_items_to_add'));
        return;
      }

      if (regularItems.length === 0 && customItems.length > 0 && !hasCustomVariant) {
        this.showNotification(getString('notifications.custom_items_only_quote'), 'error');
        return;
      }

      const btn = document.querySelector('.nf-projekt-add-to-cart');
      if (btn) {
        btn.classList.add('is-loading');
        btn.disabled = true;
      }

      try {
        const cartItems = regularItems.map(item => ({
          id: parseInt(item.variantId),
          quantity: item.quantity
        }));

        const customLineItems = [];
        if (customItems.length > 0 && hasCustomVariant) {
          const invalidCustomItems = [];
          customItems.forEach(item => {
            const rawPrice = item.rawPrice || parseFloat(item.price.replace(/[^\d,.-]/g, '').replace(',', '.'));
            if (!Number.isFinite(rawPrice) || rawPrice <= 0) {
              invalidCustomItems.push(item);
              return;
            }
            const roundedQty = Math.round(rawPrice);
            if (Math.abs(rawPrice - roundedQty) > 0.001) {
              invalidCustomItems.push(item);
              return;
            }

            const properties = {};
            const titleLabel = getString('labels.custom_item_property');
            const descriptionLabel = getString('labels.custom_item_description');
            if (titleLabel) properties[titleLabel] = item.title;
            if (descriptionLabel && item.variantTitle) properties[descriptionLabel] = item.variantTitle;

            customLineItems.push({
              id: customVariantId,
              quantity: roundedQty,
              properties: properties
            });
          });

          if (invalidCustomItems.length > 0) {
            this.showNotification(getString('notifications.custom_item_invalid_price'), 'error');
            return;
          }
        }

        if (cartItems.length === 0 && customLineItems.length === 0) {
          this.showNotification(getString('notifications.no_items_to_add'));
          return;
        }

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items: [...cartItems, ...customLineItems] })
        });

        const responseData = await response.json();

        if (response.ok) {
          if (customItems.length > 0 && !hasCustomVariant) {
            this.showNotification(formatString(getString('notifications.items_added_with_custom'), { count: regularItems.length }));
          } else {
            this.showNotification(getString('notifications.items_added_all'));
          }

          // Update cart count
          this.updateCartCount();

          // Open cart drawer using theme's ajaxCart
          if (typeof ajaxCart !== 'undefined' && ajaxCart.load) {
            ajaxCart.load();
          }

          // Dispatch events for theme compatibility
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh'));
          document.dispatchEvent(new CustomEvent('cart:item-added'));
          document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
        } else {
          const errorMsg = responseData.description || responseData.message || getString('notifications.unknown_error');
          throw new Error(errorMsg);
        }
      } catch (e) {
        console.error('Error adding to cart', e);
        this.showNotification(formatString(getString('notifications.error_prefix'), { message: e.message }));
      } finally {
        if (btn) {
          btn.classList.remove('is-loading');
          btn.disabled = false;
        }
      }
    },

    // Create Draft Order via Cloudflare Worker (admin only)
    async createDraftOrder() {
      if (this.items.length === 0) {
        this.showNotification(getString('notifications.no_items_in_project'));
        return;
      }

      const sidebar = document.getElementById('nf-projekt-sidebar');
      const workerUrl = sidebar?.dataset.draftOrderWorker;

      if (!workerUrl) {
        this.showNotification(getString('notifications.worker_not_configured'), 'error');
        console.error('Draft Order Worker URL not configured. Set draft_order_worker_url in theme settings.');
        return;
      }

      const btn = document.querySelector('.nf-projekt-create-quote');
      if (btn) {
        btn.classList.add('is-loading');
        btn.disabled = true;
      }

      try {
        // Separate regular items and custom items
        const regularItems = this.items.filter(item => !item.isCustom).map(item => ({
          variantId: item.variantId,
          quantity: item.quantity
        }));

        const customItems = this.items.filter(item => item.isCustom).map(item => ({
          title: item.title,
          price: item.rawPrice || parseFloat(item.price.replace(/[^\d,.-]/g, '').replace(',', '.')),
          quantity: item.quantity,
          description: item.variantTitle || '',
          style: item.customStyle || '',
          finish: item.customFinish || '',
          color: item.customColor || '',
          size: item.customSize || ''
        }));

        // Read discount values
        const discountInput = document.getElementById('nf-projekt-discount-value');
        const discountTypeSelect = document.getElementById('nf-projekt-discount-type');
        const discountValue = parseFloat(discountInput?.value) || 0;
        const discountType = discountTypeSelect?.value || 'percentage';

        // Read second discount values
        const discount2Input = document.getElementById('nf-projekt-discount2-value');
        const discount2TypeSelect = document.getElementById('nf-projekt-discount2-type');
        const discount2Value = parseFloat(discount2Input?.value) || 0;
        const discount2Type = discount2TypeSelect?.value || 'percentage';

        // Read shipping cost
        const shippingSelect = document.getElementById('nf-projekt-shipping-select');
        const shippingCustom = document.getElementById('nf-projekt-shipping-custom');
        let shippingCost = null; // null = default (shop shipping)
        if (shippingSelect && shippingSelect.value !== 'default') {
          shippingCost = shippingSelect.value === 'custom'
            ? (parseFloat(shippingCustom?.value) || 0)
            : (parseFloat(shippingSelect.value) || 0);
        }

        const response = await fetch(workerUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            items: regularItems,
            customItems: customItems,
            discount: discountValue > 0 ? { value: discountValue, type: discountType } : null,
            discount2: discount2Value > 0 ? { value: discount2Value, type: discount2Type } : null,
            shippingCost: shippingCost,
            allowDiscountCodes: !(document.getElementById('nf-projekt-no-discount-codes')?.checked)
          })
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(data.error || getString('notifications.error_create_offer'));
        }

        // Show success popup with invoice URL
        this.showQuoteSuccessPopup(data.draftOrderId, data.draftOrderName, data.invoiceUrl, data.totalPrice, data.currency, data.adminUrl);

      } catch (e) {
        console.error('Error creating draft order', e);
        this.showNotification(formatString(getString('notifications.error_prefix'), { message: e.message }), 'error');
      } finally {
        if (btn) {
          btn.classList.remove('is-loading');
          btn.disabled = false;
        }
      }
    },

    // Show quote success popup with invoice URL
    showQuoteSuccessPopup(draftOrderId, draftOrderName, invoiceUrl, totalPrice, currency, adminUrl) {
      const formattedTotal = parseFloat(totalPrice).toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }) + ' €';

      const popupHtml = `
        <div class="nf-projekt-popup-overlay" id="nf-projekt-quote-popup">
          <div class="nf-projekt-popup nf-projekt-popup--wide">
            <div class="nf-projekt-popup-header">
              <h3 class="nf-projekt-popup-title">${getString('quote.title')}</h3>
              <button type="button" class="nf-projekt-popup-close" aria-label="${getString('aria.close')}">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div class="nf-projekt-popup-body">
              <div class="nf-projekt-quote-success">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#28a745" stroke-width="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
                <p style="margin: 15px 0; font-size: 1.1em;">${getString('quote.total_label')}: <strong>${formattedTotal}</strong></p>
              </div>

              <div class="nf-projekt-quote-section" style="margin-bottom: 15px;">
                <div class="nf-projekt-form-group">
                  <label class="nf-projekt-form-label" for="nf-agent-select">${getString('quote.agent_name_label')}</label>
                  <select id="nf-agent-select" class="nf-projekt-form-input" style="appearance: auto;">
                    <option value="">— ${getString('quote.agent_name_placeholder')} —</option>
                    <option value="Heidi Palinkasch">Heidi Palinkasch</option>
                    <option value="Lukas Breme">Lukas Breme</option>
                    <option value="__custom">Andere(r)...</option>
                  </select>
                  <input type="text" id="nf-agent-name" class="nf-projekt-form-input" placeholder="${getString('quote.agent_name_placeholder')}" style="display: none; margin-top: 8px;">
                </div>
              </div>

              <div class="nf-projekt-quote-section" style="margin-bottom: 15px;">
                <div class="nf-projekt-form-group">
                  <label class="nf-projekt-form-label" for="nf-quote-notes">${getString('quote.notes_label')}</label>
                  <textarea id="nf-quote-notes" class="nf-projekt-form-input" rows="3"
                    placeholder="${getString('quote.notes_placeholder')}" style="width: 100%; resize: vertical; font-family: inherit;"></textarea>
                </div>
              </div>

              <div class="nf-projekt-quote-section">
                <h4 class="nf-projekt-quote-section-title">${getString('quote.send_section_title')}</h4>

                <div class="nf-projekt-customer-tabs">
                  <button type="button" class="nf-projekt-customer-tab is-active" data-tab="search">${getString('quote.tabs.search')}</button>
                  <button type="button" class="nf-projekt-customer-tab" data-tab="manual">${getString('quote.tabs.manual')}</button>
                </div>

                <div class="nf-projekt-customer-panel" id="nf-panel-search">
                  <div class="nf-projekt-search-wrapper">
                    <input type="email" id="nf-customer-search" class="nf-projekt-form-input" placeholder="${getString('quote.search_placeholder')}" autocomplete="off">
                    <div class="nf-projekt-search-results" id="nf-customer-results" style="display: none;"></div>
                  </div>
                  <div class="nf-projekt-selected-customer" id="nf-selected-customer" style="display: none;">
                    <div class="nf-projekt-customer-card">
                      <div class="nf-projekt-customer-info">
                        <strong id="nf-customer-name"></strong>
                        <span id="nf-customer-email"></span>
                        <span id="nf-customer-address"></span>
                      </div>
                      <button type="button" class="nf-projekt-customer-remove" id="nf-customer-remove">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                <div class="nf-projekt-customer-panel" id="nf-panel-manual" style="display: none;">
                  <div class="nf-projekt-form-row">
                    <div class="nf-projekt-form-group">
                      <label class="nf-projekt-form-label" for="nf-manual-firstname">${getString('quote.manual.first_name_label')}</label>
                      <input type="text" id="nf-manual-firstname" class="nf-projekt-form-input" placeholder="${getString('quote.manual.first_name_placeholder')}">
                    </div>
                    <div class="nf-projekt-form-group">
                      <label class="nf-projekt-form-label" for="nf-manual-lastname">${getString('quote.manual.last_name_label')}</label>
                      <input type="text" id="nf-manual-lastname" class="nf-projekt-form-input" placeholder="${getString('quote.manual.last_name_placeholder')}">
                    </div>
                  </div>
                  <div class="nf-projekt-form-group">
                    <label class="nf-projekt-form-label" for="nf-manual-email">${getString('quote.manual.email_label')}</label>
                    <input type="email" id="nf-manual-email" class="nf-projekt-form-input" placeholder="${getString('quote.manual.email_placeholder')}" required>
                  </div>
                  <div class="nf-projekt-form-group">
                    <label class="nf-projekt-form-label" for="nf-manual-address">${getString('quote.manual.address_label')}</label>
                    <input type="text" id="nf-manual-address" class="nf-projekt-form-input" placeholder="${getString('quote.manual.address_placeholder')}">
                  </div>
                  <div class="nf-projekt-form-row">
                    <div class="nf-projekt-form-group" style="flex: 0 0 100px;">
                      <label class="nf-projekt-form-label" for="nf-manual-zip">${getString('quote.manual.zip_label')}</label>
                      <input type="text" id="nf-manual-zip" class="nf-projekt-form-input" placeholder="${getString('quote.manual.zip_placeholder')}">
                    </div>
                    <div class="nf-projekt-form-group">
                      <label class="nf-projekt-form-label" for="nf-manual-city">${getString('quote.manual.city_label')}</label>
                      <input type="text" id="nf-manual-city" class="nf-projekt-form-input" placeholder="${getString('quote.manual.city_placeholder')}">
                    </div>
                  </div>
                </div>

                <div class="nf-popup-block" style="margin-top: 12px;">
                  <button type="button" class="nf-projekt-btn nf-projekt-send-invoice" id="nf-send-invoice" disabled>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right: 6px;">
                      <path d="M22 2L11 13"></path>
                      <path d="M22 2L15 22L11 13L2 9L22 2Z"></path>
                    </svg>
                    ${getString('quote.send_button')}
                  </button>
                </div>

              </div>

              <div class="nf-projekt-quote-divider">
                <span>${getString('quote.divider_text')}</span>
              </div>

              <div class="nf-popup-block">
                <button type="button" class="nf-projekt-btn nf-projekt-download-pdf" id="nf-download-pdf" disabled>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right: 6px;">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                  ${getString('quote.pdf_button')}
                </button>
              </div>

              <div class="nf-projekt-quote-divider">
                <span>${getString('quote.divider_text')}</span>
              </div>

              <div class="nf-popup-block">
                <input type="text" class="nf-projekt-share-url" value="${invoiceUrl}" readonly style="display: block; width: 100%; padding: 10px; margin-bottom: 10px; border: 1px solid #ddd; border-radius: 4px; font-size: 0.9em;">
                <div style="display: flex; gap: 8px;">
                  <button type="button" class="nf-projekt-share-btn primary" data-action="copy-quote-url" style="flex: 1;">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right: 6px;">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                    ${getString('quote.copy_link_button')}
                  </button>
                  <button type="button" class="nf-projekt-share-btn secondary" data-action="open-quote" style="flex: 1;">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right: 6px;">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                      <polyline points="15 3 21 3 21 9"></polyline>
                      <line x1="10" y1="14" x2="21" y2="3"></line>
                    </svg>
                    ${getString('quote.open_offer_button')}
                  </button>
                </div>
              </div>
              ${adminUrl ? `
              <div class="nf-popup-block" style="margin-top: 12px; text-align: center;">
                <a href="${adminUrl}" target="_blank" rel="noopener" class="nf-projekt-btn" style="display: inline-flex; align-items: center; gap: 6px; background: #f0f0f0; color: #333; text-decoration: none;">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                  In Shopify bearbeiten
                </a>
              </div>` : ''}
            </div>
          </div>
        </div>
      `;

      document.body.insertAdjacentHTML('beforeend', popupHtml);
      const popup = document.getElementById('nf-projekt-quote-popup');
      const sidebar = document.getElementById('nf-projekt-sidebar');
      const workerUrl = sidebar?.dataset.draftOrderWorker;

      let selectedCustomer = null;
      let searchTimeout = null;

      requestAnimationFrame(() => {
        popup.classList.add('is-active');
      });

      popup.querySelector('.nf-projekt-popup-close').addEventListener('click', () => this.closePopup(popup));
      popup.addEventListener('click', (e) => {
        if (e.target === popup) this.closePopup(popup);
      });

      // Agent dropdown: show/hide custom input
      const agentSelect = popup.querySelector('#nf-agent-select');
      const agentInput = popup.querySelector('#nf-agent-name');
      if (agentSelect) {
        agentSelect.addEventListener('change', () => {
          if (agentSelect.value === '__custom') {
            agentInput.style.display = 'block';
            agentInput.focus();
          } else {
            agentInput.style.display = 'none';
            agentInput.value = '';
          }
        });
      }
      const getAgentName = () => {
        const sel = agentSelect?.value || '';
        if (sel === '__custom') return agentInput?.value.trim() || '';
        return sel;
      };

      const getNotes = () => {
        return popup.querySelector('#nf-quote-notes')?.value.trim() || '';
      };

      popup.querySelector('[data-action="copy-quote-url"]').addEventListener('click', async () => {
        await this.copyToClipboard(invoiceUrl);
        this.showNotification(getString('notifications.link_copied'));
      });

      popup.querySelector('[data-action="open-quote"]').addEventListener('click', () => {
        window.open(invoiceUrl, '_blank');
      });

      // Tab switching
      let activeTab = 'search';
      const tabs = popup.querySelectorAll('.nf-projekt-customer-tab');
      const panelSearch = popup.querySelector('#nf-panel-search');
      const panelManual = popup.querySelector('#nf-panel-manual');
      const pdfBtn = popup.querySelector('#nf-download-pdf');
      const pdfHint = popup.querySelector('.nf-projekt-pdf-hint');

      // Helper to get current customer data
      const getCustomerData = () => {
        if (activeTab === 'search' && selectedCustomer) {
          return {
            name: `${selectedCustomer.firstName || ''} ${selectedCustomer.lastName || ''}`.trim(),
            email: selectedCustomer.email,
            address: selectedCustomer.address?.address1 || '',
            zipCity: selectedCustomer.address ? `${selectedCustomer.address.zip || ''} ${selectedCustomer.address.city || ''}`.trim() : ''
          };
        } else if (activeTab === 'manual') {
          const firstName = popup.querySelector('#nf-manual-firstname')?.value.trim() || '';
          const lastName = popup.querySelector('#nf-manual-lastname')?.value.trim() || '';
          return {
            name: `${firstName} ${lastName}`.trim(),
            email: popup.querySelector('#nf-manual-email')?.value.trim() || '',
            address: popup.querySelector('#nf-manual-address')?.value.trim() || '',
            zipCity: `${popup.querySelector('#nf-manual-zip')?.value.trim() || ''} ${popup.querySelector('#nf-manual-city')?.value.trim() || ''}`.trim()
          };
        }
        return null;
      };

      // Helper to update button states
      const updateButtonStates = () => {
        const customerData = getCustomerData();
        const hasCustomer = customerData && customerData.email;
        pdfBtn.disabled = !hasCustomer;
        if (pdfHint) {
          pdfHint.style.display = hasCustomer ? 'none' : 'block';
        }
      };

      tabs.forEach(tab => {
        tab.addEventListener('click', () => {
          activeTab = tab.dataset.tab;
          tabs.forEach(t => t.classList.remove('is-active'));
          tab.classList.add('is-active');

          if (activeTab === 'search') {
            panelSearch.style.display = 'block';
            panelManual.style.display = 'none';
            sendBtn.disabled = !selectedCustomer;
          } else {
            panelSearch.style.display = 'none';
            panelManual.style.display = 'block';
            const manualEmail = popup.querySelector('#nf-manual-email').value.trim();
            sendBtn.disabled = !manualEmail;
          }
          updateButtonStates();
        });
      });

      // Customer search
      const searchInput = popup.querySelector('#nf-customer-search');
      const resultsContainer = popup.querySelector('#nf-customer-results');
      const selectedContainer = popup.querySelector('#nf-selected-customer');
      const sendBtn = popup.querySelector('#nf-send-invoice');

      // Manual entry email validation
      const manualEmailInput = popup.querySelector('#nf-manual-email');
      manualEmailInput.addEventListener('input', () => {
        if (activeTab === 'manual') {
          sendBtn.disabled = !manualEmailInput.value.trim();
          updateButtonStates();
        }
      });

      // PDF download
      pdfBtn.addEventListener('click', async () => {
        const customerData = getCustomerData();
        if (!customerData || !customerData.email) {
          this.showNotification(getString('notifications.customer_required'), 'error');
          return;
        }

        const agentName = getAgentName();

        pdfBtn.disabled = true;
        pdfBtn.textContent = getString('notifications.pdf_generating');

        try {
          const notes = getNotes();
          await this.generatePDF(draftOrderId, formattedTotal, invoiceUrl, customerData, agentName, notes, false, draftOrderName);
          this.showNotification(getString('notifications.pdf_downloaded'));
        } catch (err) {
          console.error('PDF generation failed', err);
          this.showNotification(getString('notifications.pdf_failed'), 'error');
        } finally {
          pdfBtn.disabled = false;
          pdfBtn.innerHTML = `
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right: 6px;">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            ${getString('quote.pdf_button')}
          `;
        }
      });

      searchInput.addEventListener('input', (e) => {
        const query = e.target.value.trim();
        clearTimeout(searchTimeout);

        if (query.length < 3) {
          resultsContainer.style.display = 'none';
          return;
        }

        searchTimeout = setTimeout(async () => {
          try {
            const response = await fetch(`${workerUrl}/search-customers?q=${encodeURIComponent(query)}`);
            const data = await response.json();

            if (data.customers && data.customers.length > 0) {
              resultsContainer.innerHTML = data.customers.map(c => `
                <div class="nf-projekt-search-result" data-customer='${JSON.stringify(c)}'>
                  <strong>${c.firstName || ''} ${c.lastName || ''}</strong>
                  <span>${c.email}</span>
                  ${c.address ? `<small>${c.address.city || ''}</small>` : ''}
                </div>
              `).join('');
              resultsContainer.style.display = 'block';
            } else {
              resultsContainer.innerHTML = `<div class="nf-projekt-search-empty">${getString('quote.search_empty')}</div>`;
              resultsContainer.style.display = 'block';
            }
          } catch (err) {
            console.error('Customer search failed', err);
            resultsContainer.innerHTML = `<div class="nf-projekt-search-empty">${getString('quote.search_failed')}</div>`;
            resultsContainer.style.display = 'block';
          }
        }, 300);
      });

      resultsContainer.addEventListener('click', (e) => {
        const result = e.target.closest('.nf-projekt-search-result');
        if (result && result.dataset.customer) {
          selectedCustomer = JSON.parse(result.dataset.customer);
          popup.querySelector('#nf-customer-name').textContent = `${selectedCustomer.firstName || ''} ${selectedCustomer.lastName || ''}`.trim() || getString('quote.customer_fallback');
          popup.querySelector('#nf-customer-email').textContent = selectedCustomer.email;
          popup.querySelector('#nf-customer-address').textContent = selectedCustomer.address
            ? `${selectedCustomer.address.address1 || ''}, ${selectedCustomer.address.zip || ''} ${selectedCustomer.address.city || ''}`
            : '';

          searchInput.style.display = 'none';
          resultsContainer.style.display = 'none';
          selectedContainer.style.display = 'block';
          sendBtn.disabled = false;
          updateButtonStates();
        }
      });

      popup.querySelector('#nf-customer-remove').addEventListener('click', () => {
        selectedCustomer = null;
        searchInput.value = '';
        searchInput.style.display = 'block';
        selectedContainer.style.display = 'none';
        sendBtn.disabled = true;
        updateButtonStates();
      });

      sendBtn.addEventListener('click', async () => {
        let emailToSend = null;

        if (activeTab === 'search') {
          if (!selectedCustomer) return;
          emailToSend = selectedCustomer.email;
        } else {
          emailToSend = popup.querySelector('#nf-manual-email').value.trim();
          if (!emailToSend) return;
        }

        const agentName = getAgentName();

        sendBtn.disabled = true;
        sendBtn.textContent = getString('quote.send_in_progress');

        try {
          const customerData = getCustomerData();
          const notes = getNotes();
          const pdfBase64 = await this.generatePDF(draftOrderId, formattedTotal, invoiceUrl, customerData, agentName, notes, true, draftOrderName);

          const response = await fetch(`${workerUrl}/send-pdf-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: emailToSend,
              customerName: customerData?.name || '',
              agentName: agentName,
              notes: notes,
              invoiceUrl: invoiceUrl,
              pdfBase64: pdfBase64,
              draftOrderId: draftOrderId,
              draftOrderName: draftOrderName
            })
          });

          const data = await response.json();

          if (data.success) {
            this.showNotification(formatString(getString('notifications.quote_sent_to'), { email: emailToSend }));
            this.closePopup(popup);
          } else {
            throw new Error(data.error || getString('notifications.send_failed'));
          }
        } catch (err) {
          console.error('Send invoice failed', err);
          this.showNotification(formatString(getString('notifications.error_prefix'), { message: err.message }), 'error');
          sendBtn.disabled = false;
          sendBtn.innerHTML = `
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right: 6px;">
              <path d="M22 2L11 13"></path>
              <path d="M22 2L15 22L11 13L2 9L22 2Z"></path>
            </svg>
            ${getString('quote.send_button')}
          `;
        }
      });
    },

    // Update cart count in header (shows number of line items, not total quantity)
    async updateCartCount() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        // Use items.length (number of products) instead of item_count (total quantity)
        const lineItemCount = cart.items ? cart.items.length : 0;
        const countElements = document.querySelectorAll('.cart-count, [data-cart-count], #cart-count');
        countElements.forEach(el => {
          el.textContent = lineItemCount;
        });
      } catch (e) {
        console.error('Could not update cart count', e);
      }
    },

    // Show notification
    showNotification(message, type = 'success') {
      // Remove existing notification
      const existing = document.querySelector('.nf-projekt-notification');
      if (existing) existing.remove();

      const notification = document.createElement('div');
      notification.className = 'nf-projekt-notification';
      if (type === 'error') {
        notification.classList.add('is-error');
      }
      notification.textContent = message;
      document.body.appendChild(notification);

      // Trigger animation
      requestAnimationFrame(() => {
        notification.classList.add('is-visible');
      });

      // Remove after delay
      setTimeout(() => {
        notification.classList.remove('is-visible');
        setTimeout(() => notification.remove(), 300);
      }, 2500);
    },

    // Bind all events
    bindEvents() {
      // Heart button clicks - always add item (increases quantity if already exists)
      document.addEventListener('click', (e) => {
        const heartBtn = e.target.closest('.nf-projekt-btn-add');
        if (heartBtn) {
          e.preventDefault();
          e.stopPropagation();

          const form = heartBtn.closest('form');
          let variantId = heartBtn.dataset.variantId;
          let variantTitle = heartBtn.dataset.variantTitle || '';
          let price = heartBtn.dataset.productPrice;
          let comparePrice = heartBtn.dataset.comparePrice || '';
          let image = heartBtn.dataset.productImage;

          // Use the shared resolver from collection-configurator.js (resolves from live dropdown state)
          if (form && typeof window.configuratorResolveVariant === 'function') {
            const resolved = window.configuratorResolveVariant(form);
            if (resolved && resolved.variantOption) {
              const opt = resolved.variantOption;
              variantId = opt.value;
              price = opt.dataset.price || price;
              comparePrice = opt.dataset.comparePrice || comparePrice;
              if (opt.dataset.image) {
                image = opt.dataset.image;
              }
              // Build variant title from actual dropdown display text (not matched variant)
              const optionSelects = form.querySelectorAll('.option-select');
              if (optionSelects.length > 0) {
                variantTitle = Array.from(optionSelects).map(s => {
                  const selOpt = s.options[s.selectedIndex];
                  const value = selOpt ? selOpt.text.trim() : s.value;
                  // Get the option label (e.g. "Größe", "Farbe") from sibling <label>
                  const labelEl = s.parentElement?.querySelector('label.variant-label')
                    || (s.previousElementSibling?.tagName === 'LABEL' ? s.previousElementSibling : null);
                  const label = labelEl ? labelEl.textContent.trim() : '';
                  return label ? label + ': ' + value : value;
                }).join(' / ');
              } else {
                variantTitle = opt.textContent.trim();
              }
              console.log('[NFProjekt] Resolved variant from dropdowns:', variantId, variantTitle);
            }
          } else if (form) {
            // Fallback: read from master select directly
            const masterSelect = form.querySelector('.variant-select-master');
            if (masterSelect && masterSelect.value) {
              const selectedOption = masterSelect.options[masterSelect.selectedIndex];
              if (selectedOption && selectedOption.value) {
                variantId = selectedOption.value;
                variantTitle = selectedOption.textContent.trim();
                price = selectedOption.dataset.price || price;
                comparePrice = selectedOption.dataset.comparePrice || comparePrice;
                if (selectedOption.dataset.image) {
                  image = selectedOption.dataset.image;
                }
              }
            }
          }

          const productId = heartBtn.dataset.productId;
          const title = heartBtn.dataset.productTitle;
          const url = heartBtn.dataset.productUrl;

          // Always add - if item exists, quantity is increased
          this.addItem(productId, variantId, title, price, comparePrice, image, url, variantTitle);
        }

        // Edit custom item button
        const editBtn = e.target.closest('.nf-projekt-item-edit');
        if (editBtn) {
          const variantId = editBtn.dataset.variantId;
          this.editCustomItem(variantId);
        }

        // Remove item button
        const removeBtn = e.target.closest('.nf-projekt-item-remove');
        if (removeBtn) {
          const variantId = removeBtn.dataset.variantId;
          this.removeItem(variantId);
        }

        // Quantity buttons
        const qtyBtn = e.target.closest('.nf-projekt-qty-btn');
        if (qtyBtn) {
          const variantId = qtyBtn.dataset.variantId;
          const action = qtyBtn.dataset.action;
          const item = this.items.find(i => i.variantId === variantId);
          if (item) {
            const newQty = action === 'increase' ? item.quantity + 1 : item.quantity - 1;
            if (newQty > 0) {
              this.updateQuantity(variantId, newQty);
            } else {
              this.removeItem(variantId);
            }
          }
        }

        // Share/Save button
        const shareBtn = e.target.closest('.nf-projekt-share');
        if (shareBtn) {
          this.openSharePopup();
        }

        // Contact form button
        const contactBtn = e.target.closest('.nf-projekt-contact');
        if (contactBtn) {
          this.openContactPopup();
        }

        // Add to cart button
        const addToCartBtn = e.target.closest('.nf-projekt-add-to-cart');
        if (addToCartBtn) {
          this.addAllToCart();
        }

        // Clear all button
        const clearBtn = e.target.closest('.nf-projekt-clear');
        if (clearBtn) {
          if (confirm(getString('notifications.confirm_clear'))) {
            this.clearAll();
          }
        }

        // Mobile toggle
        const mobileToggle = e.target.closest('.nf-projekt-mobile-toggle');
        if (mobileToggle) {
          e.preventDefault();
          e.stopPropagation();
          const sidebar = document.querySelector('.nf-projekt-sidebar');
          const overlay = document.querySelector('.nf-projekt-overlay');
          if (sidebar) {
            sidebar.classList.add('is-open');
            if (overlay) overlay.classList.add('is-active');
            document.body.classList.add('nf-projekt-open');
          }
        }

        // Close sidebar
        const closeBtn = e.target.closest('.nf-projekt-close');
        const overlayClick = e.target.classList.contains('nf-projekt-overlay');
        if (closeBtn || overlayClick) {
          const sidebar = document.querySelector('.nf-projekt-sidebar');
          const overlay = document.querySelector('.nf-projekt-overlay');
          if (sidebar) sidebar.classList.remove('is-open');
          if (overlay) overlay.classList.remove('is-active');
          document.body.classList.remove('nf-projekt-open');
        }

        // Admin: Custom item toggle
        const customToggle = e.target.closest('.nf-projekt-custom-toggle');
        if (customToggle) {
          const form = document.getElementById('nf-projekt-custom-form');
          if (form) {
            const isVisible = form.style.display !== 'none';
            form.style.display = isVisible ? 'none' : 'block';
            customToggle.classList.toggle('is-active', !isVisible);
          }
        }

        // Admin: Custom item cancel
        const customCancel = e.target.closest('.nf-projekt-custom-cancel');
        if (customCancel) {
          const form = document.getElementById('nf-projekt-custom-form');
          const toggle = document.getElementById('nf-projekt-custom-toggle');
          if (form) {
            form.style.display = 'none';
            form.reset();
          }
          if (toggle) toggle.classList.remove('is-active');
        }

        // Admin: Create quote button
        const createQuoteBtn = e.target.closest('.nf-projekt-create-quote');
        if (createQuoteBtn) {
          this.createDraftOrder();
        }
      });

      // Admin: Custom item form submission
      const customForm = document.getElementById('nf-projekt-custom-form');
      if (customForm) {
        customForm.addEventListener('submit', (e) => {
          e.preventDefault();
          const title = document.getElementById('nf-custom-title').value.trim();
          const price = document.getElementById('nf-custom-price').value;
          const description = document.getElementById('nf-custom-description').value.trim();
          const style = document.getElementById('nf-custom-style').value.trim();
          const finish = document.getElementById('nf-custom-finish').value.trim();
          const color = document.getElementById('nf-custom-color').value.trim();
          const size = document.getElementById('nf-custom-size').value.trim();

          if (title && price) {
            this.addCustomItem(title, parseFloat(price), description, style, finish, color, size);
            customForm.reset();
            customForm.style.display = 'none';
            const toggle = document.getElementById('nf-projekt-custom-toggle');
            if (toggle) toggle.classList.remove('is-active');
          }
        });
      }

      // Item note input
      document.addEventListener('input', (e) => {
        if (e.target.matches('.nf-projekt-item-note')) {
          const variantId = e.target.dataset.variantId;
          if (variantId) {
            this.updateItemNote(variantId, e.target.value);
          }
        }
      });

      // Update heart buttons when variant changes
      document.addEventListener('change', (e) => {
        if (e.target.matches('.option-select, .variant-select')) {
          this.updateHeartButtonForProduct(e.target);
        }
      });

      // Discount input listeners (update total price live)
      document.addEventListener('input', (e) => {
        if (e.target.id === 'nf-projekt-discount-value') {
          this.updateTotalPrice();
          var clearBtn = document.getElementById('nf-projekt-discount-clear');
          if (clearBtn) clearBtn.style.display = parseFloat(e.target.value) > 0 ? '' : 'none';
        }
      });
      document.addEventListener('change', (e) => {
        if (e.target.id === 'nf-projekt-discount-type') {
          this.updateTotalPrice();
        }
      });
      document.addEventListener('click', (e) => {
        if (e.target.closest('#nf-projekt-discount-clear')) {
          var discountInput = document.getElementById('nf-projekt-discount-value');
          if (discountInput) { discountInput.value = ''; }
          var clearBtn = document.getElementById('nf-projekt-discount-clear');
          if (clearBtn) clearBtn.style.display = 'none';
          this.updateTotalPrice();
        }
      });

      // Discount 2 input listeners (update total price live)
      document.addEventListener('input', (e) => {
        if (e.target.id === 'nf-projekt-discount2-value') {
          this.updateTotalPrice();
          var clearBtn2 = document.getElementById('nf-projekt-discount2-clear');
          if (clearBtn2) clearBtn2.style.display = parseFloat(e.target.value) > 0 ? '' : 'none';
        }
      });
      document.addEventListener('change', (e) => {
        if (e.target.id === 'nf-projekt-discount2-type') {
          this.updateTotalPrice();
        }
      });
      document.addEventListener('click', (e) => {
        if (e.target.closest('#nf-projekt-discount2-clear')) {
          var discount2Input = document.getElementById('nf-projekt-discount2-value');
          if (discount2Input) { discount2Input.value = ''; }
          var clearBtn2 = document.getElementById('nf-projekt-discount2-clear');
          if (clearBtn2) clearBtn2.style.display = 'none';
          this.updateTotalPrice();
        }
      });

      // Shipping dropdown: show/hide custom input + update total
      document.addEventListener('change', (e) => {
        if (e.target.id === 'nf-projekt-shipping-select') {
          var customInput = document.getElementById('nf-projekt-shipping-custom');
          var euroLabel = document.getElementById('nf-projekt-shipping-euro');
          if (e.target.value === 'custom') {
            if (customInput) { customInput.style.display = ''; customInput.focus(); }
            if (euroLabel) euroLabel.style.display = '';
          } else {
            if (customInput) { customInput.style.display = 'none'; customInput.value = ''; }
            if (euroLabel) euroLabel.style.display = 'none';
          }
          this.updateTotalPrice();
        }
      });
      document.addEventListener('input', (e) => {
        if (e.target.id === 'nf-projekt-shipping-custom') {
          this.updateTotalPrice();
        }
      });

      // Keep heart button data attributes in sync via variant:resolved event
      document.addEventListener('variant:resolved', (e) => {
        const form = e.target;
        if (!form) return;
        const heartBtn = form.querySelector('.nf-projekt-btn-add');
        if (heartBtn && e.detail && e.detail.variantOption) {
          const opt = e.detail.variantOption;
          heartBtn.dataset.variantId = opt.value;
          heartBtn.dataset.productPrice = opt.dataset.price || '';
          heartBtn.dataset.variantTitle = opt.textContent.trim();
          heartBtn.dataset.comparePrice = opt.dataset.comparePrice || '';
          if (opt.dataset.image) {
            heartBtn.dataset.productImage = opt.dataset.image;
          }
        }
      });

      // Load from URL on init
      this.loadFromUrl();
    },

    // Update heart button when variant changes
    updateHeartButtonForProduct(selectEl) {
      // Find the form that contains the changed select
      const form = selectEl.closest('form');
      if (!form) return;

      // Get the master select within the SAME form (not the first one in the card)
      const masterSelect = form.querySelector('.variant-select-master');
      if (!masterSelect) return;

      const selectedOption = masterSelect.options[masterSelect.selectedIndex];
      if (!selectedOption) return;

      // Only update the heart button within this specific form
      const heartBtn = form.querySelector('.nf-projekt-btn-add');
      if (heartBtn) {
        heartBtn.dataset.variantId = selectedOption.value;
        heartBtn.dataset.productPrice = selectedOption.dataset.price || '';
        heartBtn.dataset.variantTitle = selectedOption.textContent.trim();

        if (selectedOption.dataset.image) {
          heartBtn.dataset.productImage = selectedOption.dataset.image;
        }

        // Update active state
        if (this.isInProjekt(selectedOption.value)) {
          heartBtn.classList.add('is-active');
        } else {
          heartBtn.classList.remove('is-active');
        }
      }
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => NFProjekt.init());
  } else {
    NFProjekt.init();
  }

  // Expose to global scope
  window.NFProjekt = NFProjekt;
})();


